/**
 * CELL FORMATTER UTILITY
 * Format and parse cell values based on column type
 * @version 1.0.0
 * @production-ready
 */

import { ColumnDefinition } from '../types';

/**
 * Format cell value for display
 */
export const formatCellValue = (
  value: any,
  column: ColumnDefinition,
  rowData?: any
): string => {
  // Use custom formatter if provided
  if (column.format) {
    return column.format(value, rowData);
  }
  
  // Handle null/empty values
  if (value == null || value === '') {
    return '';
  }
  
  // Apply prefix/suffix
  let formatted = formatByType(value, column);
  
  if (column.prefix) {
    formatted = column.prefix + formatted;
  }
  
  if (column.suffix) {
    formatted = formatted + ' ' + column.suffix;
  }
  
  return formatted;
};

/**
 * Format value based on column type
 */
const formatByType = (value: any, column: ColumnDefinition): string => {
  switch (column.type) {
    case 'number':
    case 'calculated':
      return formatNumber(value, column);
    
    case 'date':
      return formatDate(value);
    
    case 'time':
      return formatTime(value);
    
    case 'select':
      return formatSelect(value, column);
    
    default:
      return String(value);
  }
};

/**
 * Format number with proper decimals
 * Uses column.decimalPlaces if specified, otherwise defaults to 2
 */
const formatNumber = (value: any, column?: ColumnDefinition): string => {
  const decimalPlaces = column?.decimalPlaces ?? 2; // Default to 2 decimal places if not specified
  
  if (typeof value === 'number') {
    return value.toFixed(decimalPlaces);
  }
  
  const num = parseFloat(String(value));
  return isNaN(num) ? String(value) : num.toFixed(decimalPlaces);
};

const MONTH_ABBREVIATIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format date value for native inputs (YYYY-MM-DD)
 */
const formatDate = (value: any): string => {
  if (!value) return '';
  
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return String(value);
  }
};

/**
 * Human-readable date e.g., 23Nov25 (ddmmmyy format)
 */
export const formatDateDisplay = (value: any): string => {
  const iso = formatDate(value);
  if (!iso) return '';
  const [year, month, day] = iso.split('-');
  const monthIndex = Math.max(0, Math.min(11, parseInt(month, 10) - 1));
  const monthLabel = MONTH_ABBREVIATIONS[monthIndex] || month;
  // Format as ddmmmyy (e.g., 23Nov25)
  const twoDigitYear = year.slice(-2);
  return `${day}${monthLabel}${twoDigitYear}`;
};

/**
 * Format time value
 */
const formatTime = (value: any): string => {
  if (!value) return '';
  
  // If already in HH:MM format, return as is
  if (typeof value === 'string' && /^\d{2}:\d{2}$/.test(value)) {
    return value;
  }
  
  return String(value);
};

/**
 * Format select value
 */
const formatSelect = (value: any, column: ColumnDefinition): string => {
  if (!column.options) return String(value);
  
  // Handle array of strings
  if (typeof column.options[0] === 'string') {
    return String(value);
  }
  
  // Handle array of objects
  const option = (column.options as any[]).find(opt => opt.value === value);
  return option ? option.label : String(value);
};

/**
 * Parse user input to proper value type
 */
export const parseCellValue = (
  input: string,
  column: ColumnDefinition
): any => {
  // Use custom parser if provided
  if (column.parse) {
    return column.parse(input);
  }
  
  // Handle empty input
  if (input === '' || input == null) {
    return null;
  }
  
  // Parse by type
  switch (column.type) {
    case 'number':
    case 'calculated':
      return parseNumber(input);
    
    case 'date':
      return parseDate(input);
    
    case 'time':
      return parseTime(input);
    
    case 'select':
      return parseSelect(input, column);
    
    default:
      return input;
  }
};

/**
 * Parse number input
 */
const parseNumber = (input: string): number | null => {
  const num = parseFloat(input);
  return isNaN(num) ? null : num;
};

/**
 * Parse date input
 */
const parseDate = (input: string): string | null => {
  if (!input) return null;
  
  // Validate YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
    return input;
  }
  
  return null;
};

/**
 * Parse time input
 */
const parseTime = (input: string): string | null => {
  if (!input) return null;
  
  // Validate HH:MM format
  if (/^\d{2}:\d{2}$/.test(input)) {
    return input;
  }
  
  return null;
};

/**
 * Parse select input
 */
const parseSelect = (input: string, column: ColumnDefinition): any => {
  if (!column.options) return input;
  
  // Handle array of objects
  if (typeof column.options[0] !== 'string') {
    const option = (column.options as any[]).find(opt => opt.label === input || opt.value === input);
    return option ? option.value : input;
  }
  
  return input;
};

/**
 * Validate cell value
 */
export const validateCellValue = (
  value: any,
  column: ColumnDefinition,
  rowData?: any
): boolean | string => {
  // Use custom validator if provided
  if (column.validate) {
    return column.validate(value, rowData);
  }
  
  // Required check
  if (column.required && (value == null || value === '')) {
    return 'This field is required';
  }
  
  // Type-specific validation
  switch (column.type) {
    case 'number':
    case 'calculated':
      if (value != null && value !== '' && isNaN(Number(value))) {
        return 'Must be a valid number';
      }
      break;
    
    case 'date':
      if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return 'Must be a valid date (YYYY-MM-DD)';
      }
      break;
    
    case 'time':
      if (value && !/^\d{2}:\d{2}$/.test(value)) {
        return 'Must be a valid time (HH:MM)';
      }
      break;
  }
  
  return true;
};
